/**
 * Per-card file API wrapper.
 *
 * Talks to /api/cards/:cardId/files (and :filename) — see
 * apps/chaos-gateway/src/card-files.ts. Cookies (JWT) flow through
 * because apiFetch sets `credentials: 'include'`.
 *
 * Feature B4 (2026-05-22): adds support for `include_archived=true` so
 * the Artifacts tab can fetch archived files separately, and a thin
 * `promoteArchive` helper that calls POST /api/cards/:id/files/promote-archive
 * (owned by Subagent A). If those endpoints aren't deployed yet, calls
 * surface as 404 ApiError and the UI handles it gracefully.
 */
import { env } from '../../env';
import { apiFetch, apiJson, apiUploadWithProgress, ApiError } from './client';

export interface CardFile {
  name: string;
  size: number;
  modifiedAt: string;
  isDir: boolean;
  /**
   * True when the file lives under the card's `.archive/` directory.
   * Only set when `list({ includeArchived: true })` was used.
   */
  archived?: boolean;
  /**
   * Optional "from session XXX (DD-MM-YYYY)" provenance for archived
   * entries. Backend may attach it once it knows the source session.
   */
  archivedFrom?: { sessionId?: string; createdAt?: string } | null;
}

interface CardFilesListResponse {
  files: CardFile[];
  error?: string;
  message?: string;
}

interface CardFileUploadResponse {
  file: { name: string; size: number };
}

export interface CardFileArchiveStatus {
  ok?: boolean;
  promotable: boolean;
  /** Archive subdir we'd promote from (newest non-empty). */
  source: string | null;
  /** Number of files in the chosen subdir. */
  fileCount?: number;
  /** Latest modification time inside that subdir. */
  modifiedAt?: string | null;
  reason?: string;
}

export interface CardFilePromoteResponse {
  ok?: boolean;
  source: string | null;
  reason?: string;
  promotedFileCount?: number;
}

interface UploadOptions {
  cardId: string;
  file: File;
  signal?: AbortSignal;
  onProgress?: (progress: { loaded: number; total: number }) => void;
}

interface ListOptions {
  /** Pass true to include `.archive/` entries (Feature B4). */
  includeArchived?: boolean;
}

export const cardFilesApi = {
  list(cardId: string, opts: ListOptions = {}): Promise<CardFile[]> {
    const path = opts.includeArchived
      ? `/cards/${cardId}/files?include_archived=true`
      : `/cards/${cardId}/files`;
    return apiJson<CardFilesListResponse>(path).then((r) => r.files);
  },

  /**
   * Feature B4: probe whether this card has a non-empty `.archive/<sub>`
   * available to promote. Backend returns
   * `{ promotable, source?, fileCount?, modifiedAt?, reason? }`. We use
   * it to decide whether to render the "Archived files" panel.
   */
  archiveStatus(cardId: string): Promise<CardFileArchiveStatus> {
    return apiJson<CardFileArchiveStatus>(`/cards/${cardId}/files/archive-status`);
  },

  /**
   * Feature B4: Promote the latest non-empty `.archive/<sub>` snapshot
   * back into the live /workspace. Pass `force: true` to overwrite a
   * non-empty workspace (default rejects with reason `workspace_not_empty`).
   * Backend endpoint owned by Subagent A.
   */
  async promoteArchive(
    cardId: string,
    opts: { force?: boolean } = {},
  ): Promise<CardFilePromoteResponse> {
    return apiJson<CardFilePromoteResponse>(
      `/cards/${cardId}/files/promote-archive`,
      { method: 'POST', json: { force: opts.force === true } },
    );
  },

  /**
   * Upload a file into the card workspace.
   *
   * Retries a transient reset / 429 / 5xx with backoff, reports byte progress,
   * and abandons an attempt that stops moving (see `apiUploadWithProgress`).
   * NOTE: unlike a recording, this file still exists on the uploader's disk —
   * so the fix here is a reliable retry and a truthful error, NOT a duplicate
   * of the bytes in browser storage.
   *
   * WHAT IT GAINED 2026-08-16: every byte is read and counted before the first
   * request. The failure that motivated it is not hypothetical here — the
   * Artifacts tab uploads a dropped file list SEQUENTIALLY, so a file the user
   * moves or deletes while the queue is draining reaches `xhr.send` as a stale
   * handle, and until now that surfaced as `network_error` and was re-sent
   * three times before reporting a network problem that never happened.
   * Ceiling is 50 MB (413 `file_too_large`), which is now never retried.
   */
  async upload(opts: UploadOptions): Promise<{ name: string; size: number }> {
    const res = await apiUploadWithProgress<CardFileUploadResponse>(
      `/cards/${opts.cardId}/files`,
      opts.file,
      {
        filename: opts.file.name,
        ...(opts.signal ? { signal: opts.signal } : {}),
        ...(opts.onProgress
          ? { onProgress: (p): void => opts.onProgress!({ loaded: p.loaded, total: p.total }) }
          : {}),
      },
    );
    return res.file;
  },

  /**
   * URL that always SAVES the file.
   *
   * `?download=1` is explicit because the route now serves audio/video
   * `inline` (so it can be played and seeked in place). Without the flag a
   * .mp4 would open in a tab instead of downloading.
   */
  download(cardId: string, name: string): string {
    return `${cardFilesApi.mediaUrl(cardId, name)}?download=1`;
  },

  /**
   * URL that PLAYS the file in place. Audio/video comes back with its real
   * MIME type, `Content-Disposition: inline` and HTTP Range support, so the
   * shared MediaPlayer can seek in it.
   */
  mediaUrl(cardId: string, name: string): string {
    return `${env.VITE_API_BASE_URL.replace(/\/$/, '')}/cards/${cardId}/files/${encodeURIComponent(name)}`;
  },

  /**
   * M-X35: Promote a workspace file into the card KB.
   * POST /api/cards/:cardId/files/:filename/promote-to-kb
   * Returns 201 on success, throws ApiError with status 409 if already in KB.
   */
  async promoteToKb(
    cardId: string,
    filename: string,
    description?: string,
  ): Promise<{ ok: boolean; file: { id: string; filename: string; sizeBytes: number } }> {
    return apiJson(
      `/cards/${cardId}/files/${encodeURIComponent(filename)}/promote-to-kb`,
      { method: 'POST', json: description ? { description } : {} },
    );
  },

  async delete(cardId: string, name: string): Promise<void> {
    await apiFetch(`/cards/${cardId}/files/${encodeURIComponent(name)}`, { method: 'DELETE' });
  },
};

export const cardFilesKeys = {
  all: ['card-files'] as const,
  forCard: (cardId: string) => [...cardFilesKeys.all, cardId] as const,
  /**
   * Feature B4 archive query is cached separately so the
   * default Files listing doesn't get re-fetched every time the user
   * expands the archive section.
   */
  forCardArchived: (cardId: string) =>
    [...cardFilesKeys.all, cardId, 'archived'] as const,
};

// Re-export ApiError so callers in artifacts-tab can `instanceof`-test
// without pulling from two paths.
export { ApiError };
